// Entity-tree section for the sidebar engine. Renders a drag-reorderable list of
// entity rows (projects) with a single-open accordion of RBAC-gated sub-tabs.
// Every gateway-specific behaviour — context menu, edit popover, reorder
// persistence, the header's scope toggle — is INJECTED through the provider
// result, so the panel config stays pure JSON and just flags `tree: true`.

import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, Pin } from 'lucide-react';
import { cn } from '@clarix/ui';
import { useAuthStore } from '@clarix/auth-store';
import type { RowConfig, SectionConfig, SidebarProviderResult } from '@clarix/shared-types';
import {
  HoverNavRow,
  PanelEmpty,
  PanelLoading,
  PanelPill,
  PanelSectionHeader,
  SectionLabel,
} from './primitives';
import { evalPredicate, isRowActive, rowHref, type PanelCtx } from './context';
import { favouriteId } from './rowStyles';
import { readLastOpenedProject, writeLastOpenedProject } from '../lastOpenedProject';

interface Props {
  sec: SectionConfig;
  result: SidebarProviderResult;
  ctx: PanelCtx;
  onNavigate: () => void;
  /** Client-side label filter from the panel's search box. */
  matchLabel: (label: string) => boolean;
  hasQuery: boolean;
}

/**
 * A project's section tab (Overview, Modules, …) — a group child, so text only.
 * `pl-9` sits it just inside the parent's label, which starts at 30px: chevron
 * (20) + gap (2) + row padding (8).
 */
function TabRow({
  tab,
  parent,
  sectionId,
  ctx,
  onNavigate,
}: {
  tab: RowConfig;
  parent: RowConfig;
  sectionId: string;
  ctx: PanelCtx;
  onNavigate: () => void;
}) {
  const href = rowHref(tab, ctx);
  if (!href) return null;
  return (
    <HoverNavRow
      to={href}
      onNavigate={onNavigate}
      active={isRowActive(tab, ctx)}
      activeVariant="text"
      favourite={{
        id: favouriteId(sectionId, parent.id, tab.id),
        label: `${parent.label} › ${tab.label}`,
      }}
      className="pl-9"
    >
      <span className="flex-1 truncate">{tab.label}</span>
    </HoverNavRow>
  );
}

function TreeNode({
  row,
  sectionId,
  ctx,
  onNavigate,
  expanded,
  onToggle,
}: {
  row: RowConfig;
  sectionId: string;
  ctx: PanelCtx;
  onNavigate: () => void;
  expanded: boolean;
  onToggle: (open: boolean) => void;
}) {
  const href = rowHref(row, ctx);
  const active = isRowActive(row, ctx);

  // The active row keeps its place in the list rather than jumping to the top,
  // so it can sit below the fold — scroll it into view instead. `nearest`
  // scrolls only when the row is actually off-screen, so an already-visible row
  // never moves under the cursor.
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!active) return;
    rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!href) return null;

  const tabs = (row.expandable?.rows ?? []).filter((t) =>
    evalPredicate(t.visibleWhen, { ...ctx, scopeId: row.id }),
  );

  return (
    <div ref={rowRef}>
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => onToggle(!expanded)}
          aria-label="Toggle sections"
          aria-expanded={expanded}
          className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-gray-600 hover:text-gray-900"
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
          )}
        </button>
        <HoverNavRow
          to={href}
          onNavigate={onNavigate}
          active={active}
          onClick={() => onToggle(true)}
        >
          {/* A group header is chevron + label only; the chevron is its leading glyph. */}
          <span className="min-w-0 flex-1 truncate">{row.label}</span>
          {row.pinned && (
            <Pin
              className="h-2.5 w-2.5 flex-shrink-0 rotate-45 fill-indigo-500 text-indigo-500"
              strokeWidth={1.5}
            />
          )}
          {row.pills?.map((p) => (
            <PanelPill key={p.label} label={p.label} className={p.className} title={p.title} />
          ))}
        </HoverNavRow>
      </div>
      {expanded && (
        <div className="mt-0.5 space-y-0.5">
          {tabs.map((tab) => (
            <TabRow
              key={tab.id}
              tab={tab}
              parent={row}
              sectionId={sectionId}
              ctx={ctx}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SortableRow({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging ? { zIndex: 10, opacity: 0.8 } : {}),
      }}
      {...attributes}
      {...listeners}
      className={cn('sidebar-grab', isDragging && 'bg-surface rounded-lg shadow-md')}
    >
      {children}
    </div>
  );
}

export function SidebarTreeSection({ sec, result, ctx, onNavigate, matchLabel, hasQuery }: Props) {
  const [collapsed, setCollapsed] = useState(!(sec.defaultOpen ?? true));
  // Single-open accordion, seeded from the active entity in the URL.
  // `/projects/list`, `/projects/my-work` etc. match the same pattern, so only
  // a segment that is a real row id counts as an open project.
  const segment = ctx.pathname.match(/^\/projects\/([^/?]+)/)?.[1] ?? null;
  const activeId = segment && result.rows.some((r) => r.id === segment) ? segment : null;
  const userId = useAuthStore((s) => String(s.user?.sub ?? ''));
  const [expandedId, setExpandedId] = useState<string | null>(activeId);

  // Remember the last opened project so re-entering the app (no project in the
  // URL) restores its expanded tree instead of leaving the sidebar collapsed.
  useEffect(() => {
    if (!activeId || !userId) return;
    writeLastOpenedProject(userId, activeId);
  }, [activeId, userId]);

  const [restoredFor, setRestoredFor] = useState<string | null>(null);
  if (!activeId && restoredFor !== ctx.pathname && userId && result.rows.length > 0) {
    setRestoredFor(ctx.pathname);
    const last = readLastOpenedProject(userId);
    if (last && result.rows.some((r) => r.id === last)) setExpandedId(last);
  }

  // Opening an entity (e.g. from the list page) expands its tree here to match.
  // Adjusted during render, tracked per-id, so it fires once the rows finish
  // loading for an already-active entity — and only once per id, so a manual
  // collapse isn't overridden on the next render.
  const [autoExpandedFor, setAutoExpandedFor] = useState<string | null>(null);
  if (activeId && autoExpandedFor !== activeId && result.rows.some((r) => r.id === activeId)) {
    setAutoExpandedFor(activeId);
    setExpandedId(activeId);
  }

  const toggleExpanded = (id: string, open: boolean) => {
    setExpandedId(open ? id : null);
    if (open && userId) writeLastOpenedProject(userId, id);
  };

  const sensors = useSensors(
    // Long-press (250ms hold) activates drag, so a plain click still navigates.
    useSensor(PointerSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const rows = result.rows.filter((r) => matchLabel(r.label));
  const open = !sec.collapsible || !collapsed;

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !result.onReorder) return;
    const ids = rows.map((r) => r.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    result.onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  const wrap = (id: string, node: ReactNode): ReactNode =>
    result.wrapRow ? (result.wrapRow(id, node) as ReactNode) : node;

  const emptyText = hasQuery
    ? (sec.searchEmptyText ?? 'No matching results')
    : (result.emptyText ?? sec.emptyText ?? 'Nothing here yet');

  const header = sec.collapsible ? (
    <div className="mb-1 flex min-h-5 items-center gap-2 px-4">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={open}
        className="flex min-w-0 flex-1 items-center gap-1.5 transition-opacity hover:opacity-80"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-gray-400" strokeWidth={1.5} />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-gray-400" strokeWidth={1.5} />
        )}
        {sec.label && <SectionLabel>{sec.label}</SectionLabel>}
        <span className="text-muted-foreground ml-1 text-xs">{rows.length}</span>
      </button>
      {(result.headerControl as ReactNode) ?? null}
    </div>
  ) : (
    <PanelSectionHeader
      label={sec.label}
      control={(result.headerControl as ReactNode) ?? undefined}
    />
  );

  return (
    <div
      className={cn(
        (sec.label || sec.divider || sec.spaced || Boolean(result.headerControl)) &&
          'mt-4 first:mt-0',
      )}
    >
      {header}
      {open && (
        <div className="space-y-0.5 px-2">
          {result.isLoading ? (
            sec.loadingVariant === 'text' ? (
              <PanelEmpty>Loading…</PanelEmpty>
            ) : (
              <PanelLoading />
            )
          ) : rows.length === 0 ? (
            <PanelEmpty>{emptyText}</PanelEmpty>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={onDragEnd}
            >
              <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                {rows.map((row) => (
                  <Fragment key={row.id}>
                    {wrap(
                      row.id,
                      <SortableRow id={row.id}>
                        <TreeNode
                          row={row}
                          sectionId={sec.id}
                          ctx={ctx}
                          onNavigate={onNavigate}
                          expanded={expandedId === row.id}
                          onToggle={(o) => toggleExpanded(row.id, o)}
                        />
                      </SortableRow>,
                    )}
                  </Fragment>
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
      {(result.footer as ReactNode) ?? null}
    </div>
  );
}
